import useCalculateSolarProposalPricing from "~/api/hooks/useCalculateSolarProposalPricing"
import useSolarProposalFinancing from "~/api/hooks/useSolarProposalFinancing"
import useSolarProposalPricing from "~/api/hooks/useSolarProposalPricing"
import { Drawer } from "~/components/ui-components/v2"

import { solarConcertOfferBundle } from "./solar-concert-bundle"
import { solarLenderOf } from "./solar-finance-partner"
import {
  solarLoanProductBundle,
  solarSungageLoanProductBundle,
} from "./solar-financing-bundle"
import { solarPricingErrorMessage } from "./solar-pricing-error"
import { SOLAR_SETUP_DRAWER_TITLES } from "./solar-setup-paths"
import { SolarConcertSellerBanner } from "./SolarConcertSellerBanner"
import { SolarLightReachOfferPanel } from "./SolarLightReachOfferPanel"
import { SolarSungageQuoteCard } from "./SolarSungageQuoteCard"
import { PricingOutOfDateNotice } from "../ProposalPresentation/pricing/pricing-out-of-date"
import { RefreshPricingAction } from "../ProposalPresentation/pricing/refresh-pricing-action"
import { CatalogLoanProductPicker } from "../ProposalSetup/financing/CatalogLoanProductPicker"
import { ConcertOfferPicker } from "../ProposalSetup/financing/concert/ConcertOfferPicker"

export const LOAN_PRODUCT_NOT_REQUIRED_PROMPT = `Only Credit Human, Concert, Sungage and LightReach financing take a Loan Product. Choose one on Finance Partner & Equipment first.`
/** Battery's Cash copy, so the two rails read the same. */
export const CASH_NO_LOAN_PRODUCT_PROMPT = `Cash requires no Loan Product.`
export const LOAN_PRODUCT_LOAD_ERROR = `Unable to load the Loan Products.`

/**
 * The Loan Product step. Credit Human: the shared catalog picker over the
 * Solar financing read; saving moves the pricing inputs, so the out-of-date
 * hint and Refresh Pricing sit under it, and refreshing writes the payment
 * with the price. Concert: the shared offer picker over the offers Refresh
 * Pricing stored, with the seller's Concert account gate above it. Sungage:
 * the catalog picker over the funding group's products; Refresh Pricing asks
 * Sungage for the payment, and its quote card shows what Sungage returned.
 * LightReach: Get LightReach pricing and its offers, whose choice sets the
 * price, so Refresh Pricing never runs from here.
 */
export const SolarLoanProductStep = ({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const financingQuery = useSolarProposalFinancing(proposalId)
  const pricingQuery = useSolarProposalPricing(proposalId)
  const refreshPricing = useCalculateSolarProposalPricing()
  const financing = financingQuery.data ?? null
  // A first term on a never-priced proposal reads not_calculated, and needs
  // the same refresh to get its payment.
  const pricingNotCurrent =
    pricingQuery.data != null && pricingQuery.data.pricingStatus !== `current`
  const lender = solarLenderOf(financing)
  // A Sungage quote can go stale while the price stays current (an
  // incentive Sungage was sent changed), so the read's own flag counts too.
  const refreshNeeded =
    pricingNotCurrent ||
    (lender === `sungage` && financing?.refreshPricingRequired === true)
  // Concert and LightReach choose an offer; every other plan, a catalog product.
  const offerChosen = lender === `concert` || lender === `lightreach`

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={SOLAR_SETUP_DRAWER_TITLES[`loan-product`]}
      widthClass="sm:max-w-lg"
    >
      <div className="flex flex-col gap-4" data-testid="solar-loan-product-step">
        {financingQuery.isError ? (
          <p
            role="alert"
            className="text-[length:var(--step--1)] text-bp-danger"
          >
            {LOAN_PRODUCT_LOAD_ERROR}
          </p>
        ) : financing != null && !financing.loanProductRequired ? (
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            {financing.selectionStatus === `no_loan_product_required`
              ? CASH_NO_LOAN_PRODUCT_PROMPT
              : LOAN_PRODUCT_NOT_REQUIRED_PROMPT}
          </p>
        ) : null}
        {financing != null && lender === `concert` ? (
          <>
            <SolarConcertSellerBanner proposalId={proposalId} />
            <ConcertOfferPicker
              bundle={solarConcertOfferBundle}
              proposalId={proposalId}
              quote={financing.quote}
              onClose={onClose}
            />
          </>
        ) : null}
        {lender === `lightreach` ? (
          <SolarLightReachOfferPanel proposalId={proposalId} />
        ) : null}
        {!offerChosen &&
        financing?.loanProductRequired &&
        financing.selectedLoanProduct != null &&
        refreshNeeded ? (
          <div className="flex flex-wrap items-center gap-3">
            {pricingQuery.data?.pricingStatus === `out_of_date` ? (
              <PricingOutOfDateNotice testId="solar-loan-product-pricing-out-of-date" />
            ) : null}
            <RefreshPricingAction
              mutation={refreshPricing}
              variables={{ proposalId }}
              canEdit={financing.actions.selectLoanProduct.allowed}
              describeError={solarPricingErrorMessage}
            />
          </div>
        ) : null}
        {financing != null && lender === `sungage` ? (
          <SolarSungageQuoteCard quote={financing.quote} />
        ) : null}
        {offerChosen ? null : (
          <CatalogLoanProductPicker
            bundle={
              lender === `sungage`
                ? solarSungageLoanProductBundle
                : solarLoanProductBundle
            }
            proposalId={proposalId}
            financing={financing?.loanProductRequired ? financing : null}
            onClose={onClose}
          />
        )}
      </div>
    </Drawer>
  )
}
